import React, { useState } from "react";
import {
  Building, Home2, House2, Buildings, Crown1, Home3, Tree, Profile2User, Map1,
  Briefcase, People, Shop, Building3, Box, Truck, Map, Key, MoneyRecive,
  TickCircle, MagicStar, ArrowDown2,
} from "iconsax-reactjs";
import {
  TAXONOMY,
  getPropertyTypes,
  getPropertyTypeDef,
  getKind,
  getApplicableFields,
  hasBhk,
  getFurnishingOptions,
  getAreaUnit,
  getPriceUnit,
  normalizeListingType,
  formatIndianNumber,
  toNumber,
  toSqft,
  suggestTitle,
  suggestDescription,
  buildListingFields,
  normalizeMealSchedule,
  describeMealSchedule,
} from "../../shared/listingRules";
import { getIntent, applyIntent, applyCategory } from "./listingFormModel";
import { getListingCardInfo } from "../../utils/listingDisplay";
import Select from "../Select";
import "./ListingFormFields.css";

const OPTIONS = TAXONOMY.options;
const WEEK_DAYS = OPTIONS.weekDays;
const TYPE_ICONS = {
  Building, Home2, House2, Buildings, Crown1, Home3, Tree, Profile2User, Map1,
  Briefcase, People, Shop, Building3, Box, Truck, Map,
};

const asOptions = (list) => list.map((o) => ({ value: o.value !== undefined ? o.value : o.id, label: o.label }));
const digitsOnly = (s) => String(s || "").replace(/[^\d]/g, "");
const decimalOnly = (s) => String(s || "").replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");

// ─── Atoms ──────────────────────────────────────────────────────────────────

function Field({ id, label, required, hint, error, children }) {
  return (
    <div className={`field lf-field ${error ? "has-error" : ""}`}>
      {label && (id
        ? <label className="field-label lf-label" htmlFor={id}>{label}{required && <span className="lf-req" aria-hidden="true">*</span>}</label>
        : <div className="field-label lf-label">{label}{required && <span className="lf-req" aria-hidden="true">*</span>}</div>)}
      {children}
      {error
        ? <div className="field-error lf-error" role="alert">{error}</div>
        : hint ? <div className="field-hint lf-hint">{hint}</div> : null}
    </div>
  );
}

function Chips({ options, value, onChange, multiple = false, allowDeselect = false, label }) {
  const selected = multiple ? (Array.isArray(value) ? value : []) : value;
  return (
    <div className="lf-chips" role="group" aria-label={label}>
      {options.map((o) => {
        const active = multiple ? selected.includes(o.value) : selected === o.value;
        return (
          <button
            key={String(o.value)}
            type="button"
            className={`lf-chip ${active ? "active" : ""}`}
            aria-pressed={active}
            onClick={() => {
              if (multiple) onChange(active ? selected.filter((x) => x !== o.value) : [...selected, o.value]);
              else if (active && allowDeselect) onChange(typeof o.value === "number" ? null : "");
              else onChange(o.value);
            }}
          >
            {active && <TickCircle size={14} variant="Bold" className="lf-chip-tick" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Switch({ checked, onChange, label, hint }) {
  return (
    <label className="lf-switch">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="lf-switch-track" aria-hidden="true"><span className="lf-switch-thumb" /></span>
      <span className="lf-switch-text">
        {label}
        {hint && <small>{hint}</small>}
      </span>
    </label>
  );
}

function TextInput({ id, value, onChange, ...rest }) {
  return <input id={id} className="input lf-input" value={value || ""} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

function NumberInput({ id, value, onChange, placeholder, suffix, decimal = false }) {
  return (
    <div className="lf-adorned">
      <input
        id={id}
        className="input lf-input"
        inputMode={decimal ? "decimal" : "numeric"}
        value={value === null || value === undefined ? "" : String(value)}
        onChange={(e) => onChange(decimal ? decimalOnly(e.target.value) : digitsOnly(e.target.value))}
        placeholder={placeholder}
      />
      {suffix && <span className="lf-suffix">{suffix}</span>}
    </div>
  );
}

function MoneyInput({ id, value, onChange, placeholder, suffix }) {
  return (
    <div className="lf-adorned lf-money">
      <span className="lf-prefix">₹</span>
      <input
        id={id}
        className="input lf-input"
        inputMode="numeric"
        value={value === null || value === undefined ? "" : String(value)}
        onChange={(e) => onChange(digitsOnly(e.target.value))}
        placeholder={placeholder}
      />
      {suffix && <span className="lf-suffix">{suffix}</span>}
    </div>
  );
}

function MoreDetails({ title = "More details (optional)", defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`lf-more ${open ? "open" : ""}`}>
      <button type="button" className="lf-more-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>{title}</span>
        <ArrowDown2 size={16} className="lf-more-chevron" />
      </button>
      {open && <div className="lf-more-body">{children}</div>}
    </div>
  );
}

const money = (v) => (toNumber(v) > 0 ? `₹${formatIndianNumber(v)}` : "");

// ─── Step 1: what is being listed ───────────────────────────────────────────

const INTENTS = [
  { id: "sell", label: "Sell", hint: "Find a buyer", Icon: MoneyRecive },
  { id: "rent", label: "Rent out", hint: "Find tenants", Icon: Key },
  { id: "pg", label: "PG / Co-living", hint: "Fill your beds", Icon: Profile2User },
];

export function PropertyStep({ values, setValues, errors = {} }) {
  const intent = getIntent(values);
  const category = values.propertyCategory || "residential";
  const types = intent && intent !== "pg"
    ? getPropertyTypes(category, values.listingType).filter((t) => t.id !== "pg")
    : [];

  return (
    <div className="lf-step">
      <Field label="What do you want to do?" required error={errors.listingType}>
        <div className="lf-intents">
          {INTENTS.map(({ id, label, hint, Icon }) => {
            const active = intent === id;
            return (
              <button
                key={id}
                type="button"
                className={`lf-intent ${active ? "active" : ""}`}
                aria-pressed={active}
                onClick={() => setValues((prev) => applyIntent(prev, id))}
              >
                <Icon size={24} variant={active ? "Bold" : "Linear"} />
                <span className="lf-intent-label">{label}</span>
                <span className="lf-intent-hint">{id === "rent" && category === "commercial" ? "Rent / lease to a business" : hint}</span>
              </button>
            );
          })}
        </div>
      </Field>

      {intent && intent !== "pg" && (
        <Field label="Property category" required error={errors.propertyCategory}>
          <Chips
            label="Property category"
            options={asOptions(TAXONOMY.propertyCategories)}
            value={category}
            onChange={(c) => setValues((prev) => applyCategory(prev, c))}
          />
        </Field>
      )}

      {types.length > 0 && (
        <Field
          label="Property type"
          required
          error={errors.propertyType}
          hint={values.listingType === "lease" ? "Commercial rentals are shown to tenants as Rent / Lease." : undefined}
        >
          <div className="lf-types">
            {types.map((t) => {
              const Icon = TYPE_ICONS[t.icon] || Building;
              const active = values.propertyType === t.id;
              return (
                <button
                  key={`${t.category}-${t.id}`}
                  type="button"
                  className={`lf-type ${active ? "active" : ""}`}
                  aria-pressed={active}
                  onClick={() => setValues((prev) => ({ ...prev, propertyType: t.id }))}
                >
                  <Icon size={22} variant={active ? "Bold" : "Linear"} />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {intent === "pg" && (
        <p className="lf-note">PG listings are priced per bed. You’ll add the rent for each room type on the Pricing step.</p>
      )}
    </div>
  );
}

// ─── Step 2: address fields (the map / link picker stays in each panel) ─────

export function LocationFields({ values, setField, errors = {} }) {
  return (
    <div className="lf-step">
      <Field id="lf-society" label="Society / building / project name" hint="Optional. Buyers often search by project name.">
        <TextInput id="lf-society" value={values.societyName} onChange={(v) => setField("societyName", v)} placeholder="e.g. Prestige Lake Ridge" />
      </Field>
      <Field id="lf-address" label="Street address" hint="Optional">
        <TextInput id="lf-address" value={values.address} onChange={(v) => setField("address", v)} placeholder="House / flat no., street, landmark" />
      </Field>
      <div className="lf-row">
        <Field id="lf-locality" label="Locality" required error={errors.locality}>
          <TextInput id="lf-locality" value={values.locality} onChange={(v) => setField("locality", v)} placeholder="e.g. HSR Layout" />
        </Field>
        <Field id="lf-city" label="City" required error={errors.city}>
          <TextInput id="lf-city" value={values.city} onChange={(v) => setField("city", v)} placeholder="e.g. Bengaluru" />
        </Field>
        <Field id="lf-pincode" label="Pincode" error={errors.pincode}>
          <TextInput
            id="lf-pincode"
            inputMode="numeric"
            maxLength={6}
            value={values.pincode}
            onChange={(v) => setField("pincode", digitsOnly(v).slice(0, 6))}
            placeholder="e.g. 560102"
          />
        </Field>
      </div>
    </div>
  );
}

// ─── Step 3: details that depend on the property type ───────────────────────

function AreaField({ values, setField, error, showType, label = "Area" }) {
  const unit = getAreaUnit(values.areaUnit);
  const sqft = toSqft(values.areaValue, values.areaUnit);
  return (
    <Field
      id="lf-area"
      label={label}
      required
      error={error}
      hint={unit.id !== "sqft" && sqft ? `≈ ${formatIndianNumber(sqft)} sq ft` : undefined}
    >
      <div className="lf-area">
        <input
          id="lf-area"
          className="input lf-input"
          inputMode="decimal"
          value={values.areaValue || ""}
          onChange={(e) => setField("areaValue", decimalOnly(e.target.value))}
          placeholder="e.g. 1200"
        />
        <Select
          className="lf-input lf-unit"
          ariaLabel="Area unit"
          value={unit.id}
          options={OPTIONS.areaUnits.map((u) => ({ value: u.id, label: u.label }))}
          onChange={(id) => setField("areaUnit", id)}
        />
      </div>
      {showType && (
        <Chips
          label="Area type"
          options={asOptions(OPTIONS.areaTypes)}
          value={values.areaType}
          onChange={(v) => setField("areaType", v)}
          allowDeselect
        />
      )}
    </Field>
  );
}

export function DetailsStep({ values, setField, errors = {} }) {
  const category = values.propertyCategory;
  const def = getPropertyTypeDef(category, values.propertyType);
  if (!def) return <p className="lf-note">Choose a property type on the first step.</p>;

  const kind = getKind(category, values.propertyType);
  const fields = getApplicableFields(category, values.propertyType, values.listingType);
  const has = (key) => fields.has(key);
  const setMeals = (meals) => {
    setField("meals", meals);
    if (meals.length === 0) setField("foodType", "");
  };
  // Meals that differ by day (say, lunch only on weekends) go in a per-day
  // plan; `meals` stays every meal served on any day.
  const setMealsByDay = (on) => {
    // Each day starts from the meals picked so far: only the odd days need a click.
    setField("mealSchedule", on ? Object.fromEntries(WEEK_DAYS.map((d) => [d.id, [...(values.meals || [])]])) : null);
  };
  const toggleMeal = (day, meal) => {
    const schedule = { ...(values.mealSchedule || {}) };
    const served = schedule[day] || [];
    schedule[day] = served.includes(meal) ? served.filter((m) => m !== meal) : [...served, meal];
    setField("mealSchedule", schedule);
    setMeals(OPTIONS.meals.map((m) => m.id).filter((m) => WEEK_DAYS.some((d) => (schedule[d.id] || []).includes(m))));
  };
  const mealPlanSummary = values.mealSchedule
    ? describeMealSchedule(normalizeMealSchedule(values.mealSchedule)) || "Same meals every day so far"
    : "";

  return (
    <div className="lf-step">
      {kind === "home" && hasBhk(category, values.propertyType) && (
        <Field label="Bedrooms" required error={errors.rooms}>
          <Chips label="Bedrooms" options={asOptions(OPTIONS.bhk)} value={toNumber(values.rooms)} onChange={(v) => setField("rooms", v)} />
        </Field>
      )}
      {kind === "home" && (
        <Field label="Bathrooms">
          <Chips label="Bathrooms" options={asOptions(OPTIONS.bathrooms)} value={toNumber(values.bathrooms)} onChange={(v) => setField("bathrooms", v)} allowDeselect />
        </Field>
      )}

      {kind === "pg" && (
        <>
          <Field label="PG for" required error={errors.pgFor}>
            <Chips label="PG for" options={asOptions(OPTIONS.pgFor)} value={values.pgFor} onChange={(v) => setField("pgFor", v)} />
          </Field>
          <Field label="Suitable for">
            <Chips label="Suitable for" multiple options={asOptions(OPTIONS.pgSuitableFor)} value={values.pgSuitableFor} onChange={(v) => setField("pgSuitableFor", v)} />
          </Field>
          <Field label="Services included">
            <Chips label="Services included" multiple options={asOptions(OPTIONS.pgServices)} value={values.pgServices} onChange={(v) => setField("pgServices", v)} />
          </Field>
          <Field label="Meals included" hint={mealPlanSummary || "Leave empty if meals aren’t provided."}>
            <Switch
              checked={!!values.mealSchedule}
              onChange={setMealsByDay}
              label="Meals vary by day"
              hint="For example, lunch only on weekends"
            />
            {values.mealSchedule ? (
              <table className="lf-meal-grid">
                <thead>
                  <tr>
                    <th scope="col">Day</th>
                    {OPTIONS.meals.map((m) => <th key={m.id} scope="col">{m.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {WEEK_DAYS.map((d) => (
                    <tr key={d.id}>
                      <th scope="row">{d.label}</th>
                      {OPTIONS.meals.map((m) => (
                        <td key={m.id}>
                          <input
                            type="checkbox"
                            checked={(values.mealSchedule[d.id] || []).includes(m.id)}
                            onChange={() => toggleMeal(d.id, m.id)}
                            aria-label={`${m.label} on ${d.label}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Chips label="Meals included" multiple options={asOptions(OPTIONS.meals)} value={values.meals} onChange={setMeals} />
            )}
          </Field>
          {Array.isArray(values.meals) && values.meals.length > 0 && (
            <Field label="Food">
              <Chips label="Food" options={asOptions(OPTIONS.foodType)} value={values.foodType} onChange={(v) => setField("foodType", v)} allowDeselect />
            </Field>
          )}
          <MoreDetails defaultOpen={!!values.gateClosingTime}>
            <Field id="lf-gate" label="Gate closing time">
              <input id="lf-gate" type="time" className="input lf-input lf-narrow" value={values.gateClosingTime || ""} onChange={(e) => setField("gateClosingTime", e.target.value)} />
            </Field>
          </MoreDetails>
        </>
      )}

      {kind !== "pg" && (
        <AreaField
          values={values}
          setField={setField}
          error={errors.areaValue}
          showType={has("areaType")}
          label={kind === "plot" ? "Plot area" : "Area"}
        />
      )}

      {has("furnishing") && (
        <Field label="Furnishing">
          <Chips label="Furnishing" options={asOptions(getFurnishingOptions(kind))} value={values.furnishing} onChange={(v) => setField("furnishing", v)} allowDeselect />
        </Field>
      )}

      {(has("floorNumber") || has("totalFloors")) && (
        <div className="lf-row">
          {has("floorNumber") && (
            <Field id="lf-floor" label="Floor number" hint="0 = ground floor" error={errors.floorNumber}>
              <NumberInput id="lf-floor" value={values.floorNumber} onChange={(v) => setField("floorNumber", v)} placeholder="e.g. 3" />
            </Field>
          )}
          <Field id="lf-floors" label={has("floorNumber") ? "Total floors" : "Number of floors"}>
            <NumberInput id="lf-floors" value={values.totalFloors} onChange={(v) => setField("totalFloors", v)} placeholder="e.g. 10" />
          </Field>
        </div>
      )}

      {has("washrooms") && (
        <Field label="Washrooms">
          <Chips label="Washrooms" options={asOptions(OPTIONS.washrooms)} value={values.washrooms} onChange={(v) => setField("washrooms", v)} allowDeselect />
        </Field>
      )}

      {(has("coveredParking") || has("openParking")) && (
        <div className="lf-row">
          <Field id="lf-covered" label="Covered parking">
            <NumberInput id="lf-covered" value={values.coveredParking} onChange={(v) => setField("coveredParking", v)} placeholder="0" />
          </Field>
          <Field id="lf-open" label="Open parking">
            <NumberInput id="lf-open" value={values.openParking} onChange={(v) => setField("openParking", v)} placeholder="0" />
          </Field>
        </div>
      )}

      {has("seats") && (
        <>
          <div className="lf-row">
            <Field id="lf-seats" label="Seats / workstations">
              <NumberInput id="lf-seats" value={values.seats} onChange={(v) => setField("seats", v)} placeholder="e.g. 40" />
            </Field>
            <Field id="lf-cabins" label="Cabins">
              <NumberInput id="lf-cabins" value={values.cabins} onChange={(v) => setField("cabins", v)} placeholder="e.g. 3" />
            </Field>
            <Field id="lf-meeting" label="Meeting rooms">
              <NumberInput id="lf-meeting" value={values.meetingRooms} onChange={(v) => setField("meetingRooms", v)} placeholder="e.g. 2" />
            </Field>
          </div>
          <Switch checked={values.pantry} onChange={(v) => setField("pantry", v)} label="Pantry / cafeteria" />
        </>
      )}

      {has("frontageFt") && (
        <>
          <Field id="lf-frontage" label="Frontage">
            <NumberInput id="lf-frontage" value={values.frontageFt} onChange={(v) => setField("frontageFt", v)} placeholder="e.g. 20" suffix="ft" decimal />
          </Field>
          <Switch checked={values.mainRoadFacing} onChange={(v) => setField("mainRoadFacing", v)} label="Faces the main road" />
        </>
      )}

      {has("clearHeightFt") && (
        <div className="lf-row">
          <Field id="lf-height" label="Clear height">
            <NumberInput id="lf-height" value={values.clearHeightFt} onChange={(v) => setField("clearHeightFt", v)} placeholder="e.g. 30" suffix="ft" decimal />
          </Field>
          <Field id="lf-docks" label="Loading docks">
            <NumberInput id="lf-docks" value={values.loadingDocks} onChange={(v) => setField("loadingDocks", v)} placeholder="e.g. 2" />
          </Field>
          <Field id="lf-power" label="Power load">
            <NumberInput id="lf-power" value={values.powerLoadKva} onChange={(v) => setField("powerLoadKva", v)} placeholder="e.g. 100" suffix="kVA" decimal />
          </Field>
        </div>
      )}

      {kind === "plot" && (
        <>
          <Field label="Facing">
            <Chips label="Facing" options={asOptions(OPTIONS.facing)} value={values.facing} onChange={(v) => setField("facing", v)} allowDeselect />
          </Field>
          <Field id="lf-road" label="Width of the road in front">
            <NumberInput id="lf-road" value={values.roadWidthFt} onChange={(v) => setField("roadWidthFt", v)} placeholder="e.g. 30" suffix="ft" decimal />
          </Field>
          <div className="lf-switches">
            <Switch checked={values.cornerPlot} onChange={(v) => setField("cornerPlot", v)} label="Corner plot" />
            <Switch checked={values.boundaryWall} onChange={(v) => setField("boundaryWall", v)} label="Boundary wall" />
          </div>
          <MoreDetails defaultOpen={!!(values.plotLength || values.plotBreadth || values.approvalAuthority)}>
            <div className="lf-row">
              <Field id="lf-length" label="Plot length">
                <NumberInput id="lf-length" value={values.plotLength} onChange={(v) => setField("plotLength", v)} suffix="ft" decimal />
              </Field>
              <Field id="lf-breadth" label="Plot breadth">
                <NumberInput id="lf-breadth" value={values.plotBreadth} onChange={(v) => setField("plotBreadth", v)} suffix="ft" decimal />
              </Field>
            </div>
            <Field id="lf-approval" label="Approved by" hint="e.g. BDA, BMRDA, DTCP, HMDA">
              <TextInput id="lf-approval" value={values.approvalAuthority} onChange={(v) => setField("approvalAuthority", v)} placeholder="Approving authority" />
            </Field>
          </MoreDetails>
        </>
      )}

      {kind === "home" && (
        <MoreDetails defaultOpen={values.balconies !== "" || !!values.facing}>
          <Field label="Balconies">
            <Chips
              label="Balconies"
              options={asOptions(OPTIONS.balconies)}
              value={values.balconies === "" || values.balconies === null || values.balconies === undefined ? null : toNumber(values.balconies)}
              onChange={(v) => setField("balconies", v === null ? "" : v)}
              allowDeselect
            />
          </Field>
          <Field label="Facing">
            <Chips label="Facing" options={asOptions(OPTIONS.facing)} value={values.facing} onChange={(v) => setField("facing", v)} allowDeselect />
          </Field>
        </MoreDetails>
      )}
    </div>
  );
}

// ─── Step 4: price and terms for the transaction ────────────────────────────

export function PricingStep({ values, setField, setValues, errors = {} }) {
  const category = values.propertyCategory;
  const kind = getKind(category, values.propertyType);
  const listingType = normalizeListingType(values.listingType, category);
  const fields = getApplicableFields(category, values.propertyType, listingType);
  const has = (key) => fields.has(key);
  const isSale = listingType === "buy";
  const priceUnit = getPriceUnit(listingType || "buy", kind);
  const price = toNumber(values.price);
  const sqft = toSqft(values.areaValue, values.areaUnit);
  const sharing = values.pgSharing && typeof values.pgSharing === "object" ? values.pgSharing : {};
  const availableNow = !values.availableFrom || String(values.availableFrom).toLowerCase().startsWith("immediate");

  const pricePreview = price > 0
    ? `${money(price)}${priceUnit ? ` ${priceUnit}` : ""}${isSale && sqft ? ` · ₹${formatIndianNumber(Math.round(price / sqft))} per sq ft` : ""}`
    : undefined;

  const toggleSharing = (type, on) => {
    setValues((prev) => {
      const next = { ...(prev.pgSharing || {}) };
      if (on) next[type] = next[type] || "";
      else delete next[type];
      return { ...prev, pgSharing: next };
    });
  };
  const setSharingRent = (type, rent) => {
    setValues((prev) => ({ ...prev, pgSharing: { ...(prev.pgSharing || {}), [type]: rent } }));
  };
  const setPossession = (possession) => {
    setValues((prev) => ({
      ...prev,
      possession,
      readyToMove: possession !== "under_construction",
      possessionDate: possession === "under_construction" ? prev.possessionDate : "",
      ageOfProperty: possession === "under_construction" ? "" : prev.ageOfProperty,
    }));
  };
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="lf-step">
      {kind === "pg" ? (
        <Field label="Rent per bed" required error={errors.pgSharing} hint="Tick the room types you offer and enter the monthly rent for one bed.">
          <div className="lf-sharing">
            {OPTIONS.pgSharing.map((s) => {
              const on = Object.prototype.hasOwnProperty.call(sharing, s.id);
              return (
                <div key={s.id} className={`lf-sharing-row ${on ? "active" : ""}`}>
                  <label className="lf-check">
                    <input type="checkbox" checked={on} onChange={(e) => toggleSharing(s.id, e.target.checked)} />
                    <span>{s.label}</span>
                  </label>
                  {on && (
                    <MoneyInput value={sharing[s.id]} onChange={(v) => setSharingRent(s.id, v)} placeholder="e.g. 9000" suffix="/ bed / month" />
                  )}
                </div>
              );
            })}
          </div>
        </Field>
      ) : (
        <Field id="lf-price" label={isSale ? "Expected price" : "Monthly rent"} required error={errors.price} hint={pricePreview}>
          <MoneyInput id="lf-price" value={values.price} onChange={(v) => setField("price", v)} placeholder={isSale ? "e.g. 8500000" : "e.g. 35000"} suffix={priceUnit} />
        </Field>
      )}

      <Switch checked={values.isNegotiable} onChange={(v) => setField("isNegotiable", v)} label="Price is negotiable" />

      {isSale && kind !== "plot" && (
        <Field label="Possession">
          <Chips label="Possession" options={asOptions(OPTIONS.possession)} value={values.possession || "ready"} onChange={setPossession} />
        </Field>
      )}
      {isSale && values.possession === "under_construction" && kind !== "plot" && (
        <Field id="lf-possession" label="Expected possession date">
          <input id="lf-possession" type="date" className="input lf-input lf-narrow" min={todayIso} value={values.possessionDate || ""} onChange={(e) => setField("possessionDate", e.target.value)} />
        </Field>
      )}
      {has("ageOfProperty") && values.possession !== "under_construction" && (
        <Field label="Age of property">
          <Chips label="Age of property" options={asOptions(OPTIONS.ageOfProperty)} value={values.ageOfProperty} onChange={(v) => setField("ageOfProperty", v)} allowDeselect />
        </Field>
      )}
      {has("saleType") && (
        <Field label="Sale type">
          <Chips label="Sale type" options={asOptions(OPTIONS.saleType)} value={values.saleType} onChange={(v) => setField("saleType", v)} allowDeselect />
        </Field>
      )}
      {has("ownership") && (
        <Field label="Ownership">
          <Chips label="Ownership" options={asOptions(OPTIONS.ownership)} value={values.ownership} onChange={(v) => setField("ownership", v)} allowDeselect />
        </Field>
      )}
      {has("reraId") && (
        <Field id="lf-rera" label="RERA registration number" hint="Optional. RERA requires it in ads for registered projects.">
          <TextInput id="lf-rera" value={values.reraId} onChange={(v) => setField("reraId", v)} placeholder="e.g. PRM/KA/RERA/1251/446/PR/171014/000123" />
        </Field>
      )}
      {has("preLeased") && (
        <>
          <Switch checked={values.preLeased} onChange={(v) => setField("preLeased", v)} label="Pre-leased (already rented to a tenant)" />
          {values.preLeased && (
            <Field id="lf-current-rent" label="Current monthly rent">
              <MoneyInput id="lf-current-rent" value={values.currentRent} onChange={(v) => setField("currentRent", v)} suffix="/ month" />
            </Field>
          )}
        </>
      )}

      {!isSale && (
        <>
          <Field label="Available from">
            <Chips
              label="Available from"
              options={[{ value: "now", label: "Immediately" }, { value: "date", label: "From a date" }]}
              value={availableNow ? "now" : "date"}
              onChange={(v) => setField("availableFrom", v === "now" ? "Immediately" : todayIso)}
            />
            {!availableNow && (
              <input type="date" className="input lf-input lf-narrow" aria-label="Available from date" min={todayIso} value={String(values.availableFrom).slice(0, 10)} onChange={(e) => setField("availableFrom", e.target.value || "Immediately")} />
            )}
          </Field>
          {has("securityDeposit") && (
            <Field id="lf-deposit" label="Security deposit" hint={money(values.securityDeposit) || "Optional"}>
              <MoneyInput id="lf-deposit" value={values.securityDeposit} onChange={(v) => setField("securityDeposit", v)} placeholder="e.g. 100000" />
            </Field>
          )}
          {has("maintenance") && (
            <>
              <Switch checked={values.maintenanceIncluded} onChange={(v) => setField("maintenanceIncluded", v)} label="Maintenance is included in the rent" />
              {!values.maintenanceIncluded && (
                <Field id="lf-maintenance" label="Maintenance" hint="Optional. Leave empty if there’s none.">
                  <MoneyInput id="lf-maintenance" value={values.maintenance} onChange={(v) => setField("maintenance", v)} suffix="/ month" />
                </Field>
              )}
            </>
          )}
          {has("preferredTenants") && (
            <Field label="Preferred tenants" hint="Leave empty if anyone can rent.">
              <Chips label="Preferred tenants" multiple options={asOptions(OPTIONS.preferredTenants)} value={values.preferredTenants} onChange={(v) => setField("preferredTenants", v)} />
            </Field>
          )}
          {(has("lockInMonths") || has("escalationPercent") || has("noticePeriodDays") || has("petsAllowed")) && (
            <MoreDetails defaultOpen={!!(values.lockInMonths || values.escalationPercent || values.noticePeriodDays || values.petsAllowed || values.nonVegAllowed)}>
              <div className="lf-row">
                {has("lockInMonths") && (
                  <Field id="lf-lockin" label="Lock-in period">
                    <NumberInput id="lf-lockin" value={values.lockInMonths} onChange={(v) => setField("lockInMonths", v)} suffix="months" />
                  </Field>
                )}
                {has("escalationPercent") && (
                  <Field id="lf-escalation" label="Yearly rent increase">
                    <NumberInput id="lf-escalation" value={values.escalationPercent} onChange={(v) => setField("escalationPercent", v)} suffix="%" decimal />
                  </Field>
                )}
                {has("noticePeriodDays") && (
                  <Field id="lf-notice" label="Notice period">
                    <NumberInput id="lf-notice" value={values.noticePeriodDays} onChange={(v) => setField("noticePeriodDays", v)} suffix="days" />
                  </Field>
                )}
              </div>
              {(has("petsAllowed") || has("nonVegAllowed")) && (
                <div className="lf-switches">
                  {has("petsAllowed") && <Switch checked={values.petsAllowed} onChange={(v) => setField("petsAllowed", v)} label="Pets allowed" />}
                  {has("nonVegAllowed") && <Switch checked={values.nonVegAllowed} onChange={(v) => setField("nonVegAllowed", v)} label="Non-veg allowed" />}
                </div>
              )}
            </MoreDetails>
          )}
        </>
      )}
    </div>
  );
}

// ─── Step 5: title, description, amenities ──────────────────────────────────

/** Compact preview of the card buyers will see, built like the saved doc. */
export function ListingSummary({ values, imageUrl }) {
  const info = getListingCardInfo(buildListingFields(values).fields);
  const specs = [info.configLabel, info.bathroomsLabel, info.areaLabel].filter(Boolean);
  return (
    <div className="lf-summary">
      {imageUrl && <img src={imageUrl} alt="" className="lf-summary-img" />}
      <div className="lf-summary-body">
        <span className={`lf-summary-badge ${info.badgeClass}`}>{info.badge}</span>
        <strong className="lf-summary-title">{values.title || suggestTitle(values)}</strong>
        <span className="lf-summary-meta">{[values.locality, values.city].filter(Boolean).join(", ")}</span>
        {specs.length > 0 && <span className="lf-summary-meta">{specs.join(" · ")}</span>}
        <span className="lf-summary-price">{info.priceLabel}{info.priceUnit ? <small> {info.priceUnit}</small> : null}</span>
      </div>
    </div>
  );
}

export function PublishFields({ values, setField, errors = {}, facilities = [] }) {
  const category = values.propertyCategory;
  const usable = facilities.filter((f) => (category === "commercial" ? f.commercial !== false : f.residential !== false));
  const amenities = usable.filter((f) => (f.group || "amenity") !== "nearby");
  const nearby = usable.filter((f) => f.group === "nearby");
  const selected = Array.isArray(values.facilities) ? values.facilities : [];
  const toggle = (id) => setField("facilities", selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const { titleMax, descriptionMax } = TAXONOMY.limits;

  const facilityChips = (list, label) => (
    <div className="lf-chips" role="group" aria-label={label}>
      {list.map((f) => {
        const active = selected.includes(f.id);
        return (
          <button key={f.id} type="button" className={`lf-chip ${active ? "active" : ""}`} aria-pressed={active} onClick={() => toggle(f.id)}>
            {active && <TickCircle size={14} variant="Bold" className="lf-chip-tick" />}
            {f.name}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="lf-step">
      <Field id="lf-title" label="Listing title" required error={errors.title} hint={`${(values.title || "").trim().length}/${titleMax}`}>
        <div className="lf-inline">
          <TextInput id="lf-title" value={values.title} onChange={(v) => setField("title", v)} maxLength={titleMax} placeholder={suggestTitle(values)} />
          <button type="button" className="btn-tonal lf-ghost-btn" onClick={() => setField("title", suggestTitle(values))}>
            <MagicStar size={16} variant="Bold" /> Suggest
          </button>
        </div>
      </Field>

      <Field id="lf-description" label="Description" error={errors.description} hint={`Optional · ${(values.description || "").length}/${descriptionMax}`}>
        <textarea
          id="lf-description"
          className="textarea lf-input lf-textarea"
          rows={4}
          maxLength={descriptionMax}
          value={values.description || ""}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="What makes this property worth a visit?"
        />
        <button
          type="button"
          className="btn-tonal lf-ghost-btn lf-self-start"
          onClick={() => {
            if (!(values.description || "").trim() || window.confirm("Replace your description with a suggested one?")) {
              setField("description", suggestDescription(values));
            }
          }}
        >
          <MagicStar size={16} variant="Bold" /> Write it for me
        </button>
      </Field>

      {amenities.length > 0 && <Field label="Amenities">{facilityChips(amenities, "Amenities")}</Field>}
      {nearby.length > 0 && <Field label="Nearby">{facilityChips(nearby, "Nearby")}</Field>}
    </div>
  );
}
